import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { media } from '../theme/tokens';
import { KpiStrip } from '../components/KpiStrip';
import { DataTable } from '../components/DataTable';
import { SimpleSelect } from '../components/SimpleSelect';
import {
  useNotationWxr,
  useNotationHeaderCompare,
  NotationWxrOptions
} from 'apiTyped/hooks/notes.query';

// --- Wine Expert Rating View ---

type WxrType = 'is_primeur' | 'is_livrable';

export const WxrView: React.FC = () => {
  const { isLoading: isLoadingHeader, headerCompare } =
    useNotationHeaderCompare();
  const [type, setType] = useState<WxrType>('is_primeur');
  const [vintage, setVintage] = useState<string>('');
  const [colorFilter, setColorFilter] = useState<string>('');
  const [appellationFilter, setAppellationFilter] = useState<string>('');
  const [rankFilter, setRankFilter] = useState<string>('');

  const wxrOptions: NotationWxrOptions = { type };
  if (vintage) wxrOptions.vintage = vintage;

  const { isLoading: isLoadingWxr, wxr } = useNotationWxr(wxrOptions);

  const wxrData = wxr as any;
  const filters = wxrData?.filters || {
    classements: [],
    appellations: [],
    vintages: [],
    colors: []
  };
  const rawData: any[] = wxrData?.data || [];

  const header = headerCompare as any;
  const clientWineNames = useMemo(
    () => new Set((header?.client_wines || []).map((w: any) => w.wine_name)),
    [header]
  );

  // Apply local filters
  const filteredData = useMemo(() => {
    let result = [...rawData];

    if (colorFilter) {
      result = result.filter((r) => r.wine_color === colorFilter);
    }
    if (appellationFilter) {
      result = result.filter((r) => r.wine_appellation === appellationFilter);
    }
    if (rankFilter) {
      result = result.filter((r) => r.wine_classement === rankFilter);
    }

    // Sort by WXR descending
    result.sort((a, b) => (b.wxr || 0) - (a.wxr || 0));

    return result;
  }, [rawData, colorFilter, appellationFilter, rankFilter]);

  // KPIs
  const kpis = useMemo(() => {
    const clientRows = filteredData.filter((r) =>
      clientWineNames.has(r.wine_name)
    );
    const wxrScores = clientRows.map((r) => r.wxr).filter(Boolean);
    const bestWxr = wxrScores.length > 0 ? Math.max(...wxrScores) : null;
    const avgWxr =
      wxrScores.length > 0
        ? wxrScores.reduce((a: number, b: number) => a + b, 0) /
          wxrScores.length
        : null;

    // Best rank for client wines
    const clientRanks = clientRows.map((r) => r.wxr_rank).filter(Boolean);
    const bestRank = clientRanks.length > 0 ? Math.min(...clientRanks) : null;

    return {
      totalWines: filteredData.length,
      bestWxr,
      avgWxr,
      bestRank
    };
  }, [filteredData, clientWineNames]);

  const columns = useMemo(
    () => [
      {
        key: 'wxr_rank',
        header: '#',
        sortable: true,
        width: '6%',
        render: (row: any) => <RankCell>{row.wxr_rank || '—'}</RankCell>
      },
      {
        key: 'wine_name',
        header: 'Wine',
        sortable: true,
        width: '28%',
        render: (row: any) => (
          <WineName $isClient={clientWineNames.has(row.wine_name)}>
            {row.wine_name || '—'}
          </WineName>
        )
      },
      {
        key: 'vintage',
        header: 'Vintage',
        sortable: true,
        width: '8%',
        render: (row: any) => row.vintage || '—'
      },
      {
        key: 'wine_appellation',
        header: 'Appellation',
        sortable: true,
        width: '14%',
        render: (row: any) => row.wine_appellation || '—'
      },
      {
        key: 'wxr',
        header: 'WxR',
        sortable: true,
        width: '10%',
        render: (row: any) => (
          <WxrScore>{row.wxr ? row.wxr.toFixed(2) : '—'}</WxrScore>
        )
      },
      {
        key: 'yxr',
        header: 'YxR',
        sortable: true,
        width: '10%',
        render: (row: any) => (
          <span
            style={{
              fontFamily: 'var(--font-mono, var(--font-sans))',
              fontSize: 12
            }}
          >
            {row.yxr ? row.yxr.toFixed(2) : '—'}
          </span>
        )
      },
      {
        key: 'gap',
        header: 'Gap',
        sortable: true,
        width: '10%',
        render: (row: any) => {
          if (!row.wxr || !row.yxr) return <span>—</span>;
          const gap = row.wxr - row.yxr;
          return (
            <GapCell $positive={gap >= 0}>
              {gap >= 0 ? '+' : ''}
              {gap.toFixed(2)}
            </GapCell>
          );
        }
      },
      {
        key: 'wine_color',
        header: 'Color',
        width: '8%',
        render: (row: any) => (
          <ColorDot $color={row.wine_color?.toLowerCase()}>
            {row.wine_color || '—'}
          </ColorDot>
        )
      }
    ],
    [clientWineNames]
  );

  const isLoading = isLoadingHeader || isLoadingWxr;

  return (
    <>
      {/* KPIs */}
      <KpiStrip
        items={[
          { label: 'Total wines', value: kpis.totalWines },
          {
            label: 'Best WxR (yours)',
            value: kpis.bestWxr ? kpis.bestWxr.toFixed(2) : '—'
          },
          {
            label: 'Best rank (yours)',
            value: kpis.bestRank ? `#${kpis.bestRank}` : '—'
          },
          {
            label: 'Avg WxR (yours)',
            value: kpis.avgWxr ? kpis.avgWxr.toFixed(2) : '—'
          }
        ]}
      />

      {/* Filters */}
      <FiltersBar>
        <DisplayToggle>
          <ToggleBtn
            $active={type === 'is_primeur'}
            onClick={() => setType('is_primeur')}
          >
            Primeur
          </ToggleBtn>
          <ToggleBtn
            $active={type === 'is_livrable'}
            onClick={() => setType('is_livrable')}
          >
            Livrable
          </ToggleBtn>
        </DisplayToggle>

        <SimpleSelect
          options={[
            { value: '', label: 'All vintages' },
            ...(filters.vintages || []).map((v: string) => ({
              value: v,
              label: v
            }))
          ]}
          value={vintage}
          onChange={(val) => setVintage(val as string)}
          minWidth="120px"
        />

        <SimpleSelect
          options={[
            { value: '', label: 'All colors' },
            ...(filters.colors || []).map((c: string) => ({
              value: c,
              label: c
            }))
          ]}
          value={colorFilter}
          onChange={(val) => setColorFilter(val as string)}
          minWidth="110px"
        />

        <SimpleSelect
          options={[
            { value: '', label: 'All appellations' },
            ...(filters.appellations || []).map((a: any) => ({
              value: a.name || a,
              label: a.name || a
            }))
          ]}
          value={appellationFilter}
          onChange={(val) => setAppellationFilter(val as string)}
          minWidth="140px"
        />

        <SimpleSelect
          options={[
            { value: '', label: 'All ranks' },
            ...(filters.classements || []).map((c: any) => ({
              value: c.name || c,
              label: c.name || c
            }))
          ]}
          value={rankFilter}
          onChange={(val) => setRankFilter(val as string)}
          minWidth="110px"
        />
      </FiltersBar>

      {/* Legend */}
      <Legend>
        <LegendItem>
          <LegendDot $color="var(--terracotta)" /> Your wines
        </LegendItem>
        <LegendItem>
          <LegendDot $color="var(--ink-faint)" /> Competitors
        </LegendItem>
        <LegendMeta>
          WxR = Wine Expert Rating (current) · YxR = Year-over-year rating · Gap
          = WxR − YxR
        </LegendMeta>
      </Legend>

      {/* Table */}
      {isLoading ? (
        <LoadingWrap>Loading Wine Expert Ratings...</LoadingWrap>
      ) : (
        <DataTable
          columns={columns}
          data={filteredData}
          emptyMessage="No WXR data available for the current filters."
        />
      )}
    </>
  );
};

// --- Styled Components ---

const FiltersBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-md);
`;

const DisplayToggle = styled.div`
  display: inline-flex;
  border: 1px solid var(--line);
  border-radius: var(--radius-sm);
  overflow: hidden;
`;

const ToggleBtn = styled.button<{ $active: boolean }>`
  border: none;
  background: ${({ $active }) =>
    $active ? 'var(--terracotta)' : 'var(--white)'};
  color: ${({ $active }) => ($active ? 'var(--white)' : 'var(--ink-muted)')};
  padding: 6px 14px;
  font-family: var(--font-sans);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ $active }) =>
      $active ? 'var(--terracotta)' : 'var(--paper)'};
  }
`;

const Legend = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  padding: 0 4px;
  font-size: 11px;
  color: var(--ink-muted);
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-weight: 500;
`;

const LegendDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $color }) => $color};
`;

const LegendMeta = styled.span`
  margin-left: auto;
  font-size: 10.5px;
  color: var(--ink-faint);
`;

const LoadingWrap = styled.div`
  padding: 48px 0;
  text-align: center;
  color: var(--ink-faint);
  font-size: 13px;
`;

const WineName = styled.span<{ $isClient: boolean }>`
  font-weight: ${({ $isClient }) => ($isClient ? 600 : 400)};
  color: ${({ $isClient }) => ($isClient ? 'var(--terracotta)' : 'var(--ink)')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 220px;
  display: inline-block;
`;

const RankCell = styled.span`
  font-family: var(--font-mono, var(--font-sans));
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-muted);
`;

const WxrScore = styled.span`
  font-family: var(--font-mono, var(--font-sans));
  font-size: 13px;
  font-weight: 700;
  color: var(--terracotta);
`;

const GapCell = styled.span<{ $positive: boolean }>`
  font-family: var(--font-mono, var(--font-sans));
  font-size: 12px;
  font-weight: 600;
  color: ${({ $positive }) =>
    $positive ? 'var(--green, #2a7d3f)' : 'var(--crimson, #c0392b)'};
`;

const ColorDot = styled.span<{ $color?: string }>`
  font-size: 11px;
  text-transform: capitalize;
  color: var(--ink-muted);
`;
